import React, { useMemo } from 'react';
import { EmptyChart } from '../bar-chart/empty-chart';
import './activity-heatmap.scss';

interface IHeatmapDatasets {
  bids?: number[];
  replies?: number[];
  opened_jobs?: number[];
  invites?: number[];
  direct_messages?: number[];
  [key: string]: number[] | undefined;
}

interface IActivityHeatmapProps {
  isLoading?: boolean;
  labels?: string[];
  datasets?: IHeatmapDatasets;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getIntensity = (value: number, max: number): number => {
  if (value === 0 || max === 0) return 0;
  const ratio = value / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
};

const formatDate = (dateStr: string): string => {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const month = MONTHS_SHORT[parseInt(parts[1], 10) - 1];
  return `${month} ${parseInt(parts[2], 10)}, ${parts[0]}`;
};

interface DayCell {
  date: string;
  score: number;
  intensity: number;
  details: string;
  dayOfWeek: number; // 0=Mon, 6=Sun
  weekIndex: number;
}

export const ActivityHeatmap = ({
  isLoading = false,
  labels = [],
  datasets = {},
}: IActivityHeatmapProps): JSX.Element => {
  const { cells, weekCount, monthLabels, maxScore } = useMemo(() => {
    if (!labels.length) return { cells: [], weekCount: 0, monthLabels: [], maxScore: 0 };

    const bids = datasets.bids || [];
    const replies = datasets.replies || [];
    const opened_jobs = datasets.opened_jobs || [];
    const invites = datasets.invites || [];
    const direct_messages = datasets.direct_messages || [];
    const offers = datasets.offers_received || [];

    // Compute activity scores
    const scores = labels.map((_, i) =>
      (bids[i] || 0) + (replies[i] || 0) + (opened_jobs[i] || 0) + (invites[i] || 0) + (direct_messages[i] || 0) + (offers[i] || 0)
    );
    const max = Math.max(...scores, 0);

    // Build day cells positioned in a weekly grid
    // First, find the day of week for the first label
    const firstDate = new Date(labels[0] + 'T00:00:00');
    // getDay(): 0=Sun,1=Mon,...,6=Sat → convert to 0=Mon,...,6=Sun
    const firstDow = (firstDate.getDay() + 6) % 7;

    const dayCells: DayCell[] = [];
    let currentWeek = 0;

    for (let i = 0; i < labels.length; i++) {
      const d = new Date(labels[i] + 'T00:00:00');
      const dow = (d.getDay() + 6) % 7;

      // Determine week index from first date
      const diffDays = Math.round((d.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
      const weekIdx = Math.floor((diffDays + firstDow) / 7);

      const score = scores[i];
      const parts: string[] = [];
      if (bids[i]) parts.push(`Proposals: ${bids[i]}`);
      if (replies[i]) parts.push(`Replies: ${replies[i]}`);
      if (opened_jobs[i]) parts.push(`Jobs opened: ${opened_jobs[i]}`);
      if (invites[i]) parts.push(`Invites: ${invites[i]}`);
      if (direct_messages[i]) parts.push(`DMs: ${direct_messages[i]}`);
      if (offers[i]) parts.push(`Offers: ${offers[i]}`);

      dayCells.push({
        date: labels[i],
        score,
        intensity: getIntensity(score, max),
        details: `${formatDate(labels[i])}\nActivity: ${score}${parts.length ? '\n' + parts.join('\n') : ''}`,
        dayOfWeek: dow,
        weekIndex: weekIdx,
      });

      currentWeek = Math.max(currentWeek, weekIdx);
    }

    // Compute month labels for the top axis
    const months: { label: string; weekStart: number }[] = [];
    let lastMonth = -1;
    for (const cell of dayCells) {
      const m = parseInt(cell.date.split('-')[1], 10) - 1;
      if (m !== lastMonth) {
        months.push({ label: MONTHS_SHORT[m], weekStart: cell.weekIndex });
        lastMonth = m;
      }
    }

    return { cells: dayCells, weekCount: currentWeek + 1, monthLabels: months, maxScore: max };
  }, [labels, datasets]);

  if (isLoading) {
    return (
      <div className="activity-heatmap">
        <div className="activity-heatmap__shimmer">
          <div className="activity-heatmap__day-labels">
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="activity-heatmap__day-label-placeholder" />
            ))}
          </div>
          <div className="activity-heatmap__grid-shimmer">
            {Array.from({ length: 53 * 7 }).map((_, i) => (
              <div key={i} className="activity-heatmap__cell-shimmer" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!cells.length) {
    return <EmptyChart />;
  }

  // Build the grid: weekCount columns x 7 rows
  const gridCells: (DayCell | null)[][] = Array.from({ length: weekCount }, () => Array(7).fill(null));
  for (const cell of cells) {
    gridCells[cell.weekIndex][cell.dayOfWeek] = cell;
  }

  return (
    <div className="activity-heatmap" style={{ '--week-count': weekCount } as React.CSSProperties}>
      <div className="activity-heatmap__scroll">
        <div className="activity-heatmap__month-labels">
          {monthLabels.map((m, i) => (
            <span
              key={i}
              className="activity-heatmap__month-label"
              style={{ gridColumnStart: m.weekStart + 1 }}
            >
              {m.label}
            </span>
          ))}
        </div>
        <div className="activity-heatmap__body">
          <div className="activity-heatmap__day-labels">
            {DAYS.map((day, i) => (
              <span key={i} className="activity-heatmap__day-label">
                {i % 2 === 0 ? day : ''}
              </span>
            ))}
          </div>
          <div
            className="activity-heatmap__grid"
            style={{ gridTemplateRows: 'repeat(7, 1fr)' }}
          >
          {Array.from({ length: weekCount }).map((_, w) =>
            Array.from({ length: 7 }).map((_, d) => {
              const cell = gridCells[w]?.[d];
              if (!cell) {
                return <div key={`${w}-${d}`} className="activity-heatmap__cell activity-heatmap__cell--empty" />;
              }
              return (
                <div
                  key={`${w}-${d}`}
                  className={`activity-heatmap__cell activity-heatmap__cell--level-${cell.intensity}`}
                  title={cell.details}
                  style={{ gridColumn: w + 1, gridRow: d + 1 }}
                />
              );
            })
          )}
          </div>
        </div>
      </div>
      <div className="activity-heatmap__legend">
        <span className="activity-heatmap__legend-label">Less</span>
        {[0, 1, 2, 3, 4].map(level => (
          <div key={level} className={`activity-heatmap__cell activity-heatmap__cell--level-${level}`} />
        ))}
        <span className="activity-heatmap__legend-label">More</span>
      </div>
    </div>
  );
};
